import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useToast } from './Toast';
import api from '../services/api';
import { theme } from '../styles/theme';

// Categorias canônicas de connector type (alinhadas a
// backend/src/constants/connector-categories.js). Mantidas aqui porque o
// frontend é um build separado (ESM) e não importa o módulo CommonJS do backend.
const CONNECTOR_CATEGORY_OPTIONS = [
  { value: 'communication', labelKey: 'typeForm.category.communication' },
  { value: 'healthcare', labelKey: 'typeForm.category.healthcare' },
  { value: 'crm', labelKey: 'typeForm.category.crm' },
  { value: 'finance', labelKey: 'typeForm.category.finance' },
  { value: 'productivity', labelKey: 'typeForm.category.productivity' },
  { value: 'legal', labelKey: 'typeForm.category.legal' },
  { value: 'developer', labelKey: 'typeForm.category.developer' },
  { value: 'internal', labelKey: 'typeForm.category.internal' },
];

const EMPTY_TYPE = {
  type: '', name: '', icon: '🔌', description: '', category: 'communication',
  configFields: [],
  capabilities: {
    inbound:  { type: 'none' },
    outbound: { type: 'none' },
    tools: []
  }
};

const EMPTY_CONFIG_FIELD = { name: '', label: '', type: 'text', required: false, description: '', default: '' };
const EMPTY_TOOL = {
  name: '', description: '', handler: 'api_call',
  endpoint: '', method: 'GET', acceptableStatuses: [],
  auth: { type: 'header', headerName: '', tokenField: '' },
  input_schema: { type: 'object', properties: {}, required: [] }
};

// Converte "409, 422" -> [409, 422]; ignora tokens não-numéricos
const parseStatuses = (str) =>
  String(str || '')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !Number.isNaN(n));
export default function ConnectorTypeForm({ initial, onClose, onSaved }) {
  const { t } = useTranslation('marketplace');
  const TABS = [t('connectorMgmt:typeForm.tabs.basic'), 'Config Fields', 'Inbound', 'Outbound', 'Tools'];
  const toast = useToast();
  const isEdit = !!initial;
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(() => {
    if (!initial) return EMPTY_TYPE;
    return {
      ...initial,
      configFields: initial.configFields || [],
      capabilities: {
        inbound:   initial.capabilities?.inbound  || { type: 'none' },
        outbound:  initial.capabilities?.outbound || { type: 'none' },
        tools:     initial.capabilities?.tools    || []
      }
    };
  });

  const set = (path, value) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    if (!data.type || !data.name) { toast.warning(t('connectorMgmt:typeForm.typeAndNameRequired')); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/marketplace/connector-types/${data.type}`, data);
      } else {
        await api.post('/marketplace/connector-types', data);
      }
      onSaved();
    } catch (e) {
      toast.error(t('connectorMgmt:typeForm.saveError') + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>{isEdit ? `✏️ ${t('common:edit')} — ${data.name}` : `🔌 ${t('connectorMgmt:typeForm.newType')}`}</Title>
          <Close onClick={onClose}>✕</Close>
        </Header>

        <TabBar>
          {TABS.map((label, i) => (
            <TabBtn key={label} active={tab === i} onClick={() => setTab(i)}>{label}</TabBtn>
          ))}
        </TabBar>

        <Body>
          {/* ── BÁSICO ── */}
          {tab === 0 && (
            <Section>
              <Row>
                <FG>
                  <Label>{t('connectorMgmt:typeForm.typeId')}</Label>
                  <Input value={data.type} onChange={e => set('type', e.target.value)} placeholder="ex: feegow" disabled={isEdit} />
                </FG>
                <FG>
                  <Label>{t('connectorMgmt:typeForm.icon')}</Label>
                  <Input value={data.icon} onChange={e => set('icon', e.target.value)} placeholder="🔌" />
                </FG>
              </Row>
              <FG>
                <Label>{t('common:name')} *</Label>
                <Input value={data.name} onChange={e => set('name', e.target.value)} placeholder="ex: Feegow Clínica" />
              </FG>
              <FG>
                <Label>{t('common:description')}</Label>
                <TextArea rows={2} value={data.description} onChange={e => set('description', e.target.value)} />
              </FG>
              <Row>
                <FG>
                  <Label>{t('connectorMgmt:typeForm.categoryLabel')}</Label>
                  <Select value={data.category} onChange={e => set('category', e.target.value)}>
                    {CONNECTOR_CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{t('connectorMgmt:' + opt.labelKey)}</option>
                    ))}
                  </Select>
                </FG>
                <FG>
                  <Label>{t('connectorMgmt:typeForm.audience')}</Label>
                  <Select value={data.audience || 'all'} onChange={e => set('audience', e.target.value)}>
                    <option value="all">{t('connectorMgmt:typeForm.audienceAll')}</option>
                    <option value="business">{t('connectorMgmt:typeForm.audienceBusiness')}</option>
                    <option value="personal">{t('connectorMgmt:typeForm.audiencePersonal')}</option>
                  </Select>
                </FG>
                <FG style={{ justifyContent: 'flex-end' }}>
                  <CheckLabel>
                    <input type="checkbox" checked={!!data.system} onChange={e => set('system', e.target.checked)} />
                    {t('connectorMgmt:typeForm.systemInternal')}
                  </CheckLabel>
                </FG>
              </Row>
            </Section>
          )}

          {/* ── CONFIG FIELDS ── */}
          {tab === 1 && (
            <Section>
              <Helper>{t('connectorMgmt:typeForm.configFieldsHelper')}</Helper>
              {data.configFields.map((f, i) => (
                <FieldCard key={i}>
                  <FieldCardHeader>
                    <strong>{f.name || t('connectorMgmt:typeForm.fieldN', { n: i + 1 })}</strong>
                    <RemoveBtn aria-label={t('common:remove')} onClick={() => set('configFields', data.configFields.filter((_, j) => j !== i))}>✕</RemoveBtn>
                  </FieldCardHeader>
                  <Row>
                    <FG><Label>name</Label><Input value={f.name} onChange={e => { const cf = [...data.configFields]; cf[i] = {...cf[i], name: e.target.value}; set('configFields', cf); }} placeholder="accessToken" /></FG>
                    <FG><Label>label</Label><Input value={f.label} onChange={e => { const cf = [...data.configFields]; cf[i] = {...cf[i], label: e.target.value}; set('configFields', cf); }} placeholder="Access Token" /></FG>
                  </Row>
                  <Row>
                    <FG>
                      <Label>type</Label>
                      <Select value={f.type} onChange={e => { const cf = [...data.configFields]; cf[i] = {...cf[i], type: e.target.value}; set('configFields', cf); }}>
                        <option value="text">text</option>
                        <option value="password">password</option>
                        <option value="number">number</option>
                        <option value="textarea">textarea</option>
                        <option value="select">select</option>
                      </Select>
                    </FG>
                    <FG style={{justifyContent:'center', paddingTop: 24}}>
                      <CheckLabel>
                        <input type="checkbox" checked={f.required} onChange={e => { const cf = [...data.configFields]; cf[i] = {...cf[i], required: e.target.checked}; set('configFields', cf); }} />
                        {t('connectorMgmt:typeForm.required')}
                      </CheckLabel>
                    </FG>
                  </Row>
                  <FG><Label>description</Label><Input value={f.description || ''} onChange={e => { const cf = [...data.configFields]; cf[i] = {...cf[i], description: e.target.value}; set('configFields', cf); }} placeholder={t('connectorMgmt:typeForm.fieldDescriptionPlaceholder')} /></FG>
                  <FG><Label>default ({t('connectorMgmt:typeForm.optional')})</Label><Input value={f.default || ''} onChange={e => { const cf = [...data.configFields]; cf[i] = {...cf[i], default: e.target.value}; set('configFields', cf); }} placeholder={t('connectorMgmt:typeForm.defaultValuePlaceholder')} /></FG>
                </FieldCard>
              ))}
              <AddBtn onClick={() => set('configFields', [...data.configFields, {...EMPTY_CONFIG_FIELD}])}>+ {t('connectorMgmt:typeForm.addField')}</AddBtn>
            </Section>
          )}

          {/* ── INBOUND ── */}
          {tab === 2 && (
            <Section>
              <Helper>{t('connectorMgmt:typeForm.inboundHelper')}</Helper>
              <FG>
                <Label>{t('connectorMgmt:typeForm.inboundType')}</Label>
                <Select value={data.capabilities.inbound.type} onChange={e => set('capabilities.inbound', { type: e.target.value })}>
                  <option value="none">none — {t('connectorMgmt:typeForm.inboundNone')}</option>
                  <option value="webhook">webhook — {t('connectorMgmt:typeForm.inboundWebhook')}</option>
                  <option value="polling">polling — {t('connectorMgmt:typeForm.inboundPolling')}</option>
                  <option value="websocket">websocket</option>
                </Select>
              </FG>

              {data.capabilities.inbound.type === 'webhook' && (
                <>
                  <Row>
                    <FG>
                      <Label>Path</Label>
                      <Input value={data.capabilities.inbound.path || ''} onChange={e => set('capabilities.inbound.path', e.target.value)} placeholder="/webhooks/whatsapp" />
                    </FG>
                    <FG>
                      <Label>Message Parser</Label>
                      <Input value={data.capabilities.inbound.messageParser || ''} onChange={e => set('capabilities.inbound.messageParser', e.target.value)} placeholder="whatsapp" />
                    </FG>
                  </Row>
                  <CheckLabel style={{marginBottom: 12}}>
                    <input type="checkbox" checked={data.capabilities.inbound.enabled !== false} onChange={e => set('capabilities.inbound.enabled', e.target.checked)} />
                    {t('connectorMgmt:typeForm.enabledByDefault')}
                  </CheckLabel>
                  <SubTitle>{t('connectorMgmt:typeForm.webhookVerification')}</SubTitle>
                  <Row>
                    <FG>
                      <Label>{t('connectorMgmt:typeForm.method')}</Label>
                      <Select value={data.capabilities.inbound.verification?.method || 'none'} onChange={e => set('capabilities.inbound.verification', { ...data.capabilities.inbound.verification, method: e.target.value })}>
                        <option value="none">none</option>
                        <option value="query_param">query_param</option>
                        <option value="header">header</option>
                        <option value="body">body</option>
                      </Select>
                    </FG>
                    <FG>
                      <Label>Param Name</Label>
                      <Input value={data.capabilities.inbound.verification?.paramName || ''} onChange={e => set('capabilities.inbound.verification', { ...data.capabilities.inbound.verification, paramName: e.target.value })} placeholder="hub.verify_token" />
                    </FG>
                    <FG>
                      <Label>Config Field</Label>
                      <Input value={data.capabilities.inbound.verification?.configField || ''} onChange={e => set('capabilities.inbound.verification', { ...data.capabilities.inbound.verification, configField: e.target.value })} placeholder="webhookVerifyToken" />
                    </FG>
                  </Row>
                </>
              )}

              {data.capabilities.inbound.type === 'polling' && (
                <>
                  <Row>
                    <FG>
                      <Label>{t('connectorMgmt:typeForm.protocol')}</Label>
                      <Select value={data.capabilities.inbound.protocol || 'imap'} onChange={e => set('capabilities.inbound.protocol', e.target.value)}>
                        <option value="imap">imap</option>
                        <option value="http">http</option>
                      </Select>
                    </FG>
                    <FG>
                      <Label>{t('connectorMgmt:typeForm.intervalMs')}</Label>
                      <Input type="number" value={data.capabilities.inbound.interval || 60000} onChange={e => set('capabilities.inbound.interval', parseInt(e.target.value))} />
                    </FG>
                    <FG>
                      <Label>Message Parser</Label>
                      <Input value={data.capabilities.inbound.messageParser || ''} onChange={e => set('capabilities.inbound.messageParser', e.target.value)} placeholder="email" />
                    </FG>
                  </Row>
                  <SubTitle>{t('connectorMgmt:typeForm.pollingConfig')}</SubTitle>
                  <Helper>{t('connectorMgmt:typeForm.pollingConfigHelper')}</Helper>
                  <TextArea rows={5} value={JSON.stringify(data.capabilities.inbound.config || {}, null, 2)} onChange={e => { try { set('capabilities.inbound.config', JSON.parse(e.target.value)); } catch {} }} />
                </>
              )}
            </Section>
          )}

          {/* ── OUTBOUND ── */}
          {tab === 3 && (
            <Section>
              <Helper>{t('connectorMgmt:typeForm.outboundHelper')}</Helper>
              <FG>
                <Label>{t('connectorMgmt:typeForm.outboundType')}</Label>
                <Select value={data.capabilities.outbound.type} onChange={e => set('capabilities.outbound', { type: e.target.value })}>
                  <option value="none">none — {t('connectorMgmt:typeForm.outboundNone')}</option>
                  <option value="http">http — API REST</option>
                  <option value="smtp">smtp — email</option>
                  <option value="websocket">websocket</option>
                </Select>
              </FG>

              {data.capabilities.outbound.type === 'http' && (
                <>
                  <Row>
                    <FG style={{flex: 2}}>
                      <Label>Endpoint</Label>
                      <Input value={data.capabilities.outbound.endpoint || ''} onChange={e => set('capabilities.outbound.endpoint', e.target.value)} placeholder="https://graph.facebook.com/v18.0/{phoneNumberId}/messages" />
                    </FG>
                    <FG>
                      <Label>{t('connectorMgmt:typeForm.method')}</Label>
                      <Select value={data.capabilities.outbound.method || 'POST'} onChange={e => set('capabilities.outbound.method', e.target.value)}>
                        <option>POST</option><option>GET</option><option>PUT</option><option>PATCH</option>
                      </Select>
                    </FG>
                  </Row>
                  <SubTitle>Auth</SubTitle>
                  <Row>
                    <FG>
                      <Label>{t('connectorMgmt:typeForm.type')}</Label>
                      <Select value={data.capabilities.outbound.auth?.type || 'bearer'} onChange={e => set('capabilities.outbound.auth', { ...data.capabilities.outbound.auth, type: e.target.value })}>
                        <option value="bearer">bearer</option>
                        <option value="basic">basic</option>
                        <option value="header">header</option>
                        <option value="none">none</option>
                      </Select>
                    </FG>
                    <FG>
                      <Label>Token Field (configField)</Label>
                      <Input value={data.capabilities.outbound.auth?.tokenField || ''} onChange={e => set('capabilities.outbound.auth', { ...data.capabilities.outbound.auth, tokenField: e.target.value })} placeholder="accessToken" />
                    </FG>
                  </Row>
                  <SubTitle>Body Template (JSON)</SubTitle>
                  <Helper>{t('connectorMgmt:typeForm.bodyTemplateHelper', { field: '{field}' })}</Helper>
                  <TextArea rows={6} value={JSON.stringify(data.capabilities.outbound.bodyTemplate || {}, null, 2)} onChange={e => { try { set('capabilities.outbound.bodyTemplate', JSON.parse(e.target.value)); } catch {} }} />
                </>
              )}

              {data.capabilities.outbound.type === 'smtp' && (
                <>
                  <SubTitle>{t('connectorMgmt:typeForm.smtpConfig')}</SubTitle>
                  <Helper>{t('connectorMgmt:typeForm.smtpConfigHelper')}</Helper>
                  <TextArea rows={5} value={JSON.stringify(data.capabilities.outbound.config || {}, null, 2)} onChange={e => { try { set('capabilities.outbound.config', JSON.parse(e.target.value)); } catch {} }} />
                </>
              )}
            </Section>
          )}

          {/* ── TOOLS ── */}
          {tab === 4 && (
            <Section>
              <Helper>{t('connectorMgmt:typeForm.toolsHelper')}</Helper>
              {data.capabilities.tools.map((tool, i) => (
                <FieldCard key={i}>
                  <FieldCardHeader>
                    <strong>{tool.name || t('connectorMgmt:typeForm.toolN', { n: i + 1 })}</strong>
                    <RemoveBtn aria-label={t('common:remove')} onClick={() => set('capabilities.tools', data.capabilities.tools.filter((_, j) => j !== i))}>✕</RemoveBtn>
                  </FieldCardHeader>
                  <Row>
                    <FG>
                      <Label>name</Label>
                      <Input value={tool.name} onChange={e => { const t = [...data.capabilities.tools]; t[i] = {...t[i], name: e.target.value}; set('capabilities.tools', t); }} placeholder="feegow_get_patient" />
                    </FG>
                    <FG>
                      <Label>handler</Label>
                      <Select value={tool.handler} onChange={e => { const tl = [...data.capabilities.tools]; tl[i] = {...tl[i], handler: e.target.value}; set('capabilities.tools', tl); }}>
                        <option value="api_call">api_call — {t('connectorMgmt:typeForm.handlerApiCall')}</option>
                        <option value="outbound">outbound — {t('connectorMgmt:typeForm.handlerOutbound')}</option>
                        <option value="outbound_smtp">outbound_smtp — {t('connectorMgmt:typeForm.handlerOutboundSmtp')}</option>
                      </Select>
                    </FG>
                  </Row>
                  <FG>
                    <Label>description</Label>
                    <Input value={tool.description} onChange={e => { const tl = [...data.capabilities.tools]; tl[i] = {...tl[i], description: e.target.value}; set('capabilities.tools', tl); }} placeholder={t('connectorMgmt:typeForm.toolDescriptionPlaceholder')} />
                  </FG>
                  {tool.handler === 'api_call' && (
                    <Row>
                      <FG style={{flex: 2}}>
                        <Label>endpoint</Label>
                        <Input value={tool.endpoint || ''} onChange={e => { const t = [...data.capabilities.tools]; t[i] = {...t[i], endpoint: e.target.value}; set('capabilities.tools', t); }} placeholder="{baseUrl}/patients/search" />
                      </FG>
                      <FG>
                        <Label>method</Label>
                        <Select value={tool.method || 'GET'} onChange={e => { const t = [...data.capabilities.tools]; t[i] = {...t[i], method: e.target.value}; set('capabilities.tools', t); }}>
                          <option>GET</option><option>POST</option><option>PUT</option><option>DELETE</option><option>PATCH</option>
                        </Select>
                      </FG>
                    </Row>
                  )}
                  {tool.handler === 'api_call' && (
                    <FG>
                      <Label>{t('connectorMgmt:typeForm.acceptableStatuses')}</Label>
                      <Input value={(tool.acceptableStatuses || []).join(', ')} onChange={e => { const tl = [...data.capabilities.tools]; tl[i] = {...tl[i], acceptableStatuses: parseStatuses(e.target.value)}; set('capabilities.tools', tl); }} placeholder={t('connectorMgmt:typeForm.acceptableStatusesPlaceholder')} />
                    </FG>
                  )}
                  {tool.handler === 'api_call' && (
                    <>
                      <SubTitle>{t('connectorMgmt:typeForm.toolAuth')}</SubTitle>
                      <Row>
                        <FG>
                          <Label>{t('connectorMgmt:typeForm.type')}</Label>
                          <Select value={tool.auth?.type || 'header'} onChange={e => { const t = [...data.capabilities.tools]; t[i] = {...t[i], auth: {...t[i].auth, type: e.target.value}}; set('capabilities.tools', t); }}>
                            <option value="header">header</option>
                            <option value="bearer">bearer</option>
                            <option value="basic">basic</option>
                            <option value="service_account">service_account</option>
                            <option value="none">none</option>
                          </Select>
                        </FG>
                        <FG>
                          <Label>headerName</Label>
                          <Input value={tool.auth?.headerName || ''} onChange={e => { const t = [...data.capabilities.tools]; t[i] = {...t[i], auth: {...t[i].auth, headerName: e.target.value}}; set('capabilities.tools', t); }} placeholder="x-access-token" />
                        </FG>
                        <FG>
                          <Label>tokenField (configField)</Label>
                          <Input value={tool.auth?.tokenField || ''} onChange={e => { const t = [...data.capabilities.tools]; t[i] = {...t[i], auth: {...t[i].auth, tokenField: e.target.value}}; set('capabilities.tools', t); }} placeholder="apiToken" />
                        </FG>
                      </Row>
                    </>
                  )}
                  <SubTitle>input_schema (JSON)</SubTitle>
                  <TextArea rows={6} value={JSON.stringify(tool.input_schema || { type: 'object', properties: {}, required: [] }, null, 2)} onChange={e => { try { const t = [...data.capabilities.tools]; t[i] = {...t[i], input_schema: JSON.parse(e.target.value)}; set('capabilities.tools', t); } catch {} }} />
                </FieldCard>
              ))}
              <AddBtn onClick={() => set('capabilities.tools', [...data.capabilities.tools, {...EMPTY_TOOL}])}>+ {t('connectorMgmt:typeForm.addTool')}</AddBtn>
            </Section>
          )}


        </Body>

        <Footer>
          <CancelBtn onClick={onClose}>{t('cancel')}</CancelBtn>
          <SaveBtn onClick={handleSave} disabled={saving}>{saving ? t('saving', { ns: 'common' }) : isEdit ? t('save', { ns: 'common' }) : t('create', { ns: 'common' })}</SaveBtn>
        </Footer>
      </Modal>
    </Overlay>
  );
}

const Overlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:1000;`;
const Modal = styled.div`background:white;border-radius:12px;width:92%;max-width:780px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;`;
const Header = styled.div`display:flex;justify-content:space-between;align-items:center;padding:20px 24px;border-bottom:1px solid #e0e0e0;flex-shrink:0;`;
const Title = styled.h2`font-size:18px;font-weight:600;color:#2c3e50;margin:0;`;
const Close = styled.button`background:none;border:none;font-size:22px;color:#7f8c8d;cursor:pointer;&:hover{color:#2c3e50;}`;
const TabBar = styled.div`display:flex;border-bottom:2px solid #e0e0e0;flex-shrink:0;overflow-x:auto;`;
const TabBtn = styled.button`padding:10px 16px;background:none;border:none;border-bottom:3px solid ${p => p.active ? theme.colors.primary : 'transparent'};color:${p => p.active ? theme.colors.primary : theme.colors.textMuted};font-size:13px;font-weight:500;cursor:pointer;white-space:nowrap;&:hover{color:${theme.colors.primary};}`;
const Body = styled.div`padding:20px 24px;overflow-y:auto;flex:1;`;
const Footer = styled.div`display:flex;gap:12px;justify-content:flex-end;padding:16px 24px;border-top:1px solid #e0e0e0;flex-shrink:0;`;
const Section = styled.div`display:flex;flex-direction:column;gap:14px;`;
const Row = styled.div`display:flex;gap:12px;`;
const FG = styled.div`display:flex;flex-direction:column;flex:1;`;
const Label = styled.label`font-size:12px;font-weight:600;color:#555;margin-bottom:4px;`;
const Input = styled.input`padding:8px 10px;border:1px solid #ddd;border-radius:6px;font-size:13px;&:focus{outline:none;border-color:#667eea;}`;
const TextArea = styled.textarea`padding:8px 10px;border:1px solid #ddd;border-radius:6px;font-size:12px;font-family:monospace;resize:vertical;&:focus{outline:none;border-color:#667eea;}`;
const Select = styled.select`padding:8px 10px;border:1px solid #ddd;border-radius:6px;font-size:13px;background:white;&:focus{outline:none;border-color:#667eea;}`;
const Helper = styled.div`font-size:12px;color:#7f8c8d;`;
const SubTitle = styled.div`font-size:12px;font-weight:700;color:#2c3e50;margin-top:4px;`;
const FieldCard = styled.div`border:1px solid #e0e0e0;border-radius:8px;padding:14px;display:flex;flex-direction:column;gap:10px;background:#fafafa;`;
const FieldCardHeader = styled.div`display:flex;justify-content:space-between;align-items:center;`;
const RemoveBtn = styled.button`background:none;border:none;color:#e74c3c;font-size:16px;cursor:pointer;padding:0 4px;&:hover{color:#c0392b;}`;
const AddBtn = styled.button`padding:8px 16px;background:#f0f2ff;color:#667eea;border:1px dashed #667eea;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;&:hover{background:#e0e4ff;}`;
const CheckLabel = styled.label`display:flex;align-items:center;gap:8px;font-size:13px;color:#2c3e50;cursor:pointer;`;
const CancelBtn = styled.button`padding:10px 20px;background:white;color:#666;border:1px solid #ddd;border-radius:6px;font-size:14px;cursor:pointer;&:hover{background:#f8f9fa;}`;
const SaveBtn = styled.button`padding:10px 24px;background:#667eea;color:white;border:none;border-radius:6px;font-size:14px;font-weight:500;cursor:pointer;&:disabled{opacity:.6;cursor:default;}&:hover:not(:disabled){background:#5568d3;}`;
